import { useAppInsightsContext } from "@microsoft/applicationinsights-react-js";
import { encrypt } from "app/shared/utils/EncryptionUtility";
import logging from "app/shared/utils/logging";
import Constants, { AppEvents } from "app/shared/constants/constants";
import { RootState, store as ReduxStore} from "app/store";

export type GA4Events = "login" | "enter_location" | "store_selection" | "form_submit";
export type FormEvents = "Rewards" | "Personal Info" | "Check Gift Card Balance" | "Missed Reward Check-in";
export type LinkDetail = {
    linkText: string,
    linkUrl: string,
};
export type Form = {
    form_name: string,
};
export type Page = {
    user_id: string,
    store?: string,
};
export type GA4EventType = {
    event:GA4Events,
    page?:Page,
    linkDetail?: LinkDetail,
    form?:Form,
};

function getStorePayload() {
    const reduxStoreData:RootState = ReduxStore.getState();
    const {user, isGuest} = reduxStoreData.auth;
    const {storeDescription: store} = reduxStoreData.stores;
    const {appliedCoupon} = reduxStoreData.userInfo;
    const {
        orderNumber,
        itemsInCart: items,
        tax,
        totalCartPrice,
        appliedRewards,
    } = reduxStoreData.orders;

    const userId = isGuest ? "" : encrypt(user || "");
    const currency = "USD";
    const page = {user_id: userId, store: store === Constants.NO_LOCATION_SELECTED ? "" : store};
    const value = totalCartPrice;
    const transactionId = orderNumber;
    const rewardApplied = appliedRewards.join(",");
    const coupon = appliedCoupon;
    return {
        items, tax, coupon, value, transactionId, rewardApplied, currency, page,
    };
}

function useGA4Layer() {
    const appInsights = useAppInsightsContext();
    function pushEvent(eventPayload:GA4EventType) {
        try {
            window.dataLayer?.push(eventPayload);
            logging.info(appInsights, AppEvents.GA4_EVENTS, eventPayload.event, eventPayload);
        } catch (error) {
            logging.error(
                appInsights,
                AppEvents.GA4_EVENTS,
                eventPayload.event,
                { error, eventPayload },
            );
        }
    }
    function pushOtherEvent(
        event: GA4Events,
        linkDetail?:LinkDetail,
        storeName?: string,
    ) {
        try {
            const { page: { store, ...page } } = getStorePayload();
            const eventPayload:GA4EventType = {
                event,
                page,
            };
            if (linkDetail) {
                eventPayload.linkDetail = linkDetail;
            }
            if (event === "store_selection") {
                eventPayload.page = {...page, store};
            }
            if (storeName) {
                eventPayload.page = {...page, store: storeName};
            }
            pushEvent(eventPayload);
        } catch (error) {
            logging.error(
                appInsights,
                AppEvents.GA4_EVENTS,
                event,
                { error },
            );
        }
    }
    function pushFormEvent(formEvent: FormEvents) {
        const event = "form_submit";
        try {
            const { page: { store, ...page } } = getStorePayload();
            const eventPayload:GA4EventType = {
                event,
                page,
                form: {
                    form_name: formEvent,
                },
            };
            if (formEvent === "Rewards") {
                eventPayload.page = {...page, store};
            }
            pushEvent(eventPayload);
        } catch (error) {
            logging.error(
                appInsights,
                AppEvents.GA4_EVENTS,
                event,
                { error },
            );
        }
    }
    return { pushOtherEvent, pushFormEvent };
}

export default useGA4Layer;
